import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Radio, Users } from 'lucide-react';
import { useAuth } from '../Auth';
import { apiService } from '../../services/api';

const TACTICAL_PHASE_LABELS = {
  inclusion: "Tour d'inclusion",
  metrics: 'Revue des indicateurs',
  projects: 'Revue des projets',
  agenda: "Ordre du jour",
  closure: 'Cloture',
};

const meetingSortKey = (meeting) =>
  new Date(meeting?.updated_at || meeting?.started_at || meeting?.created_at || 0).getTime();

const MeetingContextBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const API_URL = apiService.getBaseUrl();
  const [openMeetings, setOpenMeetings] = useState([]);

  const fetchOpenMeetings = useCallback(async () => {
    const effectiveUserId = user?.employee_id ?? user?.id;
    if (!effectiveUserId) {
      setOpenMeetings([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/circle-meetings/open-for-user`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Open meetings fetch failed');
      const data = await response.json();
      setOpenMeetings(Array.isArray(data) ? data : []);
    } catch (err) {
      setOpenMeetings([]);
    }
  }, [API_URL, getAuthHeaders, user]);

  useEffect(() => {
    fetchOpenMeetings();
    const intervalId = setInterval(fetchOpenMeetings, 10000);
    const handleFocus = () => fetchOpenMeetings();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchOpenMeetings]);

  const primaryMeeting = useMemo(() => {
    if (!Array.isArray(openMeetings) || openMeetings.length === 0) return null;
    return [...openMeetings]
      .sort((a, b) => meetingSortKey(b) - meetingSortKey(a))[0];
  }, [openMeetings]);

  const isHolacracyRoute = location.pathname.startsWith('/holacracy');

  if (!primaryMeeting || isHolacracyRoute) {
    return null;
  }

  const meetingType = String(primaryMeeting?.type || 'gouvernance').toLowerCase();
  const isTactical = meetingType === 'tactique';
  const isPilotage = meetingType === 'pilotage';
  const phaseKey = String(primaryMeeting?.meeting_phase || 'inclusion').toLowerCase();
  const phaseLabel = isTactical
    ? (TACTICAL_PHASE_LABELS[phaseKey] || TACTICAL_PHASE_LABELS.inclusion)
    : isPilotage
      ? 'Revue des projets'
    : 'Traitement des tensions';
  const typeLabel = isTactical ? 'Tactique' : isPilotage ? 'Pilotage' : 'Gouvernance';
  const circleLabel = primaryMeeting?.circle_name || `Cercle #${primaryMeeting?.circle_id || '?'}`;
  const extraMeetingsCount = Math.max(0, openMeetings.length - 1);

  return (
    <div className="border-b border-emerald-200 bg-emerald-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex items-center gap-2 text-sm text-emerald-900">
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
              <Radio size={12} />
              Reunion active
            </span>
            <span className="truncate font-medium">
              {typeLabel} - {circleLabel}
            </span>
            <span className="hidden sm:inline text-emerald-700">|</span>
            <span className="text-emerald-700">
              Tour actuel: {phaseLabel}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {extraMeetingsCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                <Users size={12} />
                +{extraMeetingsCount} autre{extraMeetingsCount > 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={() =>
                navigate('/holacracy', {
                  state: {
                    resumeMeetingId: primaryMeeting?.id ?? null,
                    resumeCircleId: primaryMeeting?.circle_id ?? null,
                  },
                })
              }
              className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
            >
              Reprendre la reunion
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingContextBanner;
